import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/hooks/useAuth';
import { signOut } from '@/lib/api/auth';

export function Nav() {
  const { isAuthenticated, user, profile } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node))
        setDropdownOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [dropdownOpen]);

  const handleSignOut = async () => {
    setDropdownOpen(false);
    await signOut();
    navigate('/');
  };

  const displayName = profile?.display_name ?? user?.email ?? '';
  const avatarSrc = profile?.avatar_url ?? null;
  const profileHandle = profile?.handle;

  return (
    <nav className="sticky top-0 z-40 border-b border-border/60 bg-cream/85 backdrop-blur">
      <div className="container flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/brand/cpn_mark.svg" alt="" aria-hidden="true" className="h-9 w-9" />
          <span className="flex flex-col leading-tight">
            <span className="font-display text-sm font-semibold text-navy">
              The Cambodian Professionals Network
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted">
              Est. 2013 · <span className="khmer normal-case">ខ្មែរ</span>
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          {(['#story', '#pillars', '#map', '#events', '#faq', '#learn-khmer'] as const).map(
            (href) => (
              <a
                key={href}
                href={href}
                className="text-sm text-navy/80 transition-colors hover:text-navy"
              >
                {href === '#story'
                  ? 'Our story'
                  : href === '#pillars'
                  ? 'What we do'
                  : href === '#map'
                  ? 'Members'
                  : href === '#events'
                  ? 'Events'
                  : href === '#faq'
                  ? 'FAQ'
                  : 'Learn Khmer'}
              </a>
            ),
          )}
        </div>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen((o) => !o)}
                aria-expanded={dropdownOpen}
                aria-haspopup="menu"
                aria-label="Account menu"
                className="flex items-center gap-2 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/40"
              >
                <Avatar name={displayName} src={avatarSrc} size="sm" />
                <span className="hidden max-w-[120px] truncate text-xs text-navy sm:inline">
                  {displayName}
                </span>
              </button>

              {dropdownOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-border/70 bg-white py-1 shadow-lg"
                >
                  {profileHandle && (
                    <Link
                      role="menuitem"
                      to={`/profile/${profileHandle}`}
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-ink/80 hover:bg-cream-warm hover:text-navy"
                    >
                      My Profile
                    </Link>
                  )}
                  <Link
                    role="menuitem"
                    to="/me/edit"
                    onClick={() => setDropdownOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-ink/80 hover:bg-cream-warm hover:text-navy"
                  >
                    Edit Profile
                  </Link>
                  <div className="my-1 border-t border-border/60" />
                  <button
                    role="menuitem"
                    type="button"
                    onClick={handleSignOut}
                    className="flex w-full items-center px-4 py-2 text-sm text-ink/80 hover:bg-cream-warm hover:text-navy"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Button asChild variant="ghost-gold" size="sm" className="hidden sm:inline-flex">
                <Link to="/signin">Sign in</Link>
              </Button>
              <Button asChild variant="gold" size="sm">
                <Link to="/signup">Join →</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
